import { templateLocationTypes } from '@qdabra/forms-shared';
import { ifElse } from 'ramda';
import { isDocumentLocation } from '../../../formView/viewForm.formLocation';
import { parseXmlNative } from '../../utility/xmlHelpers';
import makeShPAccess from '../sharePoint/sharePointAccessFactory';
import { createTemplateAsync } from '../template/template';
import templateInfoSource from '../templateInfoSource';
import { ensureProcessingInstructions } from './ensureMainDsProcessingInstructions';
import { tryUpgradeDocumentAsync } from './loader.documentUpgrade';
import { getDocumentXmlAsync } from './loader.getDocument';
import {
    determineTemplateLocation,
    templateLocationsToLocation,
} from './loader.templateLocation';
import makeTemplateLocation from './makeTemplateLocation';

// 2023-11-01 - Code moved from loader.js

/*

Core is {
    api: QfsAccess,
    template: Template,
    mainXml: DOMDocument,
    xpathEngine: XPathEngine,
    shpAccess: SharePointAccess,
}

*/

const { qd } = window;

const { perfMark, perfMeasure } = qd.util;

const { LOCATION_TYPE_TEMPLATE_NAME } = templateLocationTypes;

const parseXml = parseXmlNative;

function processTemplateXml(data) {
    perfMark('formsviewer_mainfile_loaded');
    perfMeasure(
        'formsviewer_mainfile',
        'formsviewer_template_loaded',
        'formsviewer_mainfile_loaded',
    );

    return parseXml(data);
}

const makeCoreBuilder = (
    logging,
    apiBase,
    preloadData,
    createQfsAccess,
    showWait,
) => {
    const prepareShPAccess = (options = {}) =>
        makeShPAccess(apiBase, { showWait, ...options });

    const makeCore = (api, template, mainXml) => ({
        api,
        template,
        mainXml,
        xpathEngine: template.getXPathEngine(),
        shpAccess: prepareShPAccess({
            templateQueryMode: template.getQueryMode(),
        }),
    });

    async function getCoreForTemplateAsync() {
        // BUGBUG - Currently, forms-shared uses internal logic to look at URL parameters
        //          and build the qfsAccess service. Seems like passing them in from
        //          outside should be the standard.
        const api = createQfsAccess();
        const template = await createTemplateAsync(
            api,
            true,
            templateInfoSource(api, preloadData),
            logging,
        );

        const mainXml = processTemplateXml(
            await template.getTemplateFileAsync(template.getTemplateFileName()),
        );

        ensureProcessingInstructions(mainXml, template, template.getHrefUrl());

        return makeCore(api, template, mainXml);
    }

    // FormLocation -> Promise[DOMDocument]
    const getDocument = async (formLocation) => {
        const docRetrievalApi = createQfsAccess({
            // createQfsAccess throws an error if it can't determine some template location, so
            // providing a placeholder - the queries to get XML documents should not be affected
            // by the template name
            location: makeTemplateLocation(
                '__PLACEHOLDER__',
                LOCATION_TYPE_TEMPLATE_NAME,
            ),
        });
        // Template settings determine which mode to use for form queries
        // so we use ShP access without template settings to retrieve the file
        const docRetrievalShPAccess = prepareShPAccess();

        const documentXml = await getDocumentXmlAsync(
            docRetrievalApi,
            docRetrievalShPAccess,
            formLocation,
        );

        return documentXml;
    };

    async function getCoreForFileAsync(formLocation) {
        const documentXml = await getDocument(formLocation);

        // Currently assumes that templateLocation, when specified, is a templateName
        // when we're loading a document
        const locations = determineTemplateLocation(
            documentXml,
            formLocation.templateLocation,
        );
        const api = createQfsAccess({
            location: templateLocationsToLocation(locations),
        });

        const template = await createTemplateAsync(
            api,
            false,
            templateInfoSource(api, preloadData),
            logging,
        );

        const mainXml = await tryUpgradeDocumentAsync(
            documentXml,
            template,
            api,
        );

        ensureProcessingInstructions(mainXml, template, locations.href);

        return makeCore(api, template, mainXml);
    }

    // FormLocation -> Promise[Core]
    const getCoreAsync = ifElse(
        isDocumentLocation,
        getCoreForFileAsync,
        getCoreForTemplateAsync,
    );

    return {
        getCoreAsync,
    };
};

export default makeCoreBuilder;
